import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { fmtCount, fmtMs, fmtTokens, fmtUsd, fmtUsdCompact } from "./helpers";

// Palette for stacked feature/company series. Mirrors the muted teal/coral/violet
// theme used by the other dashboards. Recharts cycles through this list.
export const SERIES_COLORS = [
  "#2F7A8A", // teal (primary)
  "#DC6A4C", // coral
  "#8352C0", // violet
  "#1E6D5B", // jade
  "#D4A45A", // amber
  "#B5495D", // berry
  "#5A8DBF", // steel
  "#6E9367", // moss
  "#C46B96", // rose
  "#8E7CC3", // lilac
] as const;

export const PRIMARY = "#2F7A8A";
export const CORAL   = "#DC6A4C";
export const VIOLET  = "#8352C0";
export const JADE    = "#1E6D5B";
export const MUTED   = "#A5A196";
export const FG3     = "#7A766C";

export function colorFor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length] as string;
}

// Recharts tooltips default to no background and a low stacking order, so
// the chart legend shows through them. Style like an opaque card and force
// the wrapper above the legend (which sits ~z-index 1).
const TOOLTIP_CONTENT_STYLE = {
  background: "#ffffff",
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  fontSize: 11,
  padding: "8px 10px",
} as const;

const TOOLTIP_WRAPPER_STYLE = {
  zIndex: 50,
  outline: "none",
  pointerEvents: "none" as const,
};

const TOOLTIP_LABEL_STYLE = { fontSize: 11, fontWeight: 600, color: "var(--fg-1)" } as const;
const TOOLTIP_ITEM_STYLE  = { fontSize: 11 } as const;

export function KpiCard({
  label, value, sub, icon, accent,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: string;
  accent?: string;
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">
        <span>{label}</span>
        {icon && (
          <i
            className={`ti ${icon}`}
            style={{ fontSize: 14, color: accent ?? VIOLET, marginLeft: "auto" }}
          />
        )}
      </div>
      <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

// Horizontal bar chart used for the "Spend by feature" / "Top by company"
// toplists. Bars are sorted descending by value before being passed in.
export function HBarChart({
  data, color = PRIMARY, valueFormatter, valueLabel = "value",
}: {
  data: { label: string; value: number }[];
  color?: string;
  valueFormatter?: (n: number) => string;
  valueLabel?: string;
}) {
  if (data.length === 0) return <div className="empty">No data in range.</div>;
  const fmt = valueFormatter ?? ((n: number) => n.toLocaleString());
  const height = Math.max(140, data.length * 26);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 10, fill: MUTED }}
          tickFormatter={fmt}
        />
        <YAxis
          dataKey="label"
          type="category"
          tick={{ fontSize: 10, fill: FG3 }}
          width={170}
          interval={0}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.03)" }}
          contentStyle={TOOLTIP_CONTENT_STYLE}
          wrapperStyle={TOOLTIP_WRAPPER_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value: unknown) => [fmt(Number(value)), valueLabel]}
        />
        <Bar
          dataKey="value"
          fill={color}
          radius={[0, 3, 3, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Stacked-bar timeseries: one bar per bucket, stacked by feature/company.
export function StackedTimeseriesBars({
  data, seriesKeys, formatValue,
}: {
  data: Record<string, number | string>[];
  seriesKeys: string[];
  formatValue: (n: number) => string;
}) {
  if (data.length === 0 || seriesKeys.length === 0) {
    return <div className="empty">No data in range.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} interval="preserveStartEnd" />
        <YAxis
          tick={{ fontSize: 10, fill: MUTED }}
          width={60}
          tickFormatter={formatValue}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.03)" }}
          contentStyle={TOOLTIP_CONTENT_STYLE}
          wrapperStyle={TOOLTIP_WRAPPER_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          itemSorter={(item) => -Number(item.value ?? 0)}
          formatter={(value: unknown, name: unknown) => [formatValue(Number(value)), String(name)]}
        />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="square" />
        {seriesKeys.map((k, i) => (
          <Bar
            key={k}
            dataKey={k}
            stackId="cost"
            fill={colorFor(i)}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// Multi-line timeseries used for latency p95.
export function MultiLineTimeseries({
  data, seriesKeys, formatValue,
}: {
  data: Record<string, number | string>[];
  seriesKeys: string[];
  formatValue: (n: number) => string;
}) {
  if (data.length === 0 || seriesKeys.length === 0) {
    return <div className="empty">No data in range.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} interval="preserveStartEnd" />
        <YAxis
          tick={{ fontSize: 10, fill: MUTED }}
          width={60}
          tickFormatter={formatValue}
        />
        <Tooltip
          cursor={{ stroke: "rgba(0,0,0,0.1)" }}
          contentStyle={TOOLTIP_CONTENT_STYLE}
          wrapperStyle={TOOLTIP_WRAPPER_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          itemSorter={(item) => -Number(item.value ?? 0)}
          formatter={(value: unknown, name: unknown) => [formatValue(Number(value)), String(name)]}
        />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="plainline" />
        {seriesKeys.map((k, i) => (
          <Line
            key={k}
            type="monotone"
            dataKey={k}
            stroke={colorFor(i)}
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export const formatters = {
  cost: fmtUsdCompact,
  costPrecise: fmtUsd,
  count: fmtCount,
  tokens: fmtTokens,
  ms: fmtMs,
};
